import React from 'react';

const AlgorithmicOptions = ({ currentLesson, setCurrentLesson, currentState, updateTutorialState, currentTutorialInfo, userStats, setUserStats, setTutorialComplete, tutorialComplete }) => {
  
  const moduleContent = {
    1: {
      title: "Algorithmic Trading Foundations",
      icon: "🤖",
      content: {
        keyPoints: [
          "Automated decision-making and execution",
          "Systematic vs discretionary approaches",
          "Low-latency requirements and infrastructure",
          "Risk management automation and controls"
        ],
        example: "Algorithm monitors BTC vol surface, automatically executes calendar spreads when term structure inverts beyond 5% threshold."
      },
      quiz: [
        {
          id: 'q1',
          question: "Algorithmic trading's main advantage is:",
          options: ["Human intuition", "Automated systematic execution", "Manual oversight", "Emotional decision making"],
          correct: 1,
          explanation: "Correct! Algorithmic trading provides systematic, emotion-free execution at scale."
        },
        {
          id: 'q2',
          question: "Low latency is important for:",
          options: ["Long-term investing", "High-frequency strategies", "Manual trading", "Portfolio rebalancing"],
          correct: 1,
          explanation: "Correct! Low latency is critical for high-frequency strategies where speed determines profitability."
        }
      ]
    },
    2: {
      title: "Strategy Development Framework",
      icon: "🏗️",
      content: {
        keyPoints: [
          "Alpha generation and signal processing",
          "Risk model integration and position sizing",
          "Transaction cost analysis and optimization",
          "Strategy lifecycle management"
        ],
        example: "Develop mean-reversion signal: Z-score > 2 triggers short vol, Z-score < -2 triggers long vol positions."
      },
      quiz: [
        {
          id: 'q1',
          question: "Alpha in algorithmic trading refers to:",
          options: ["Greek letter", "Excess return over benchmark", "Speed of execution", "Risk measurement"],
          correct: 1,
          explanation: "Correct! Alpha represents excess returns generated by the strategy beyond market returns."
        },
        {
          id: 'q2',
          question: "Transaction cost analysis helps:",
          options: ["Increase trading volume", "Optimize execution efficiency", "Ignore market impact", "Maximize fees"],
          correct: 1,
          explanation: "Correct! Transaction cost analysis optimizes execution to minimize market impact and fees."
        }
      ]
    },
    3: {
      title: "Backtesting and Simulation",
      icon: "📈",
      content: {
        keyPoints: [
          "Historical performance validation",
          "Look-ahead bias and survivorship bias prevention",
          "Out-of-sample testing and walk-forward analysis",
          "Monte Carlo simulation and stress testing"
        ],
        example: "Backtest vol trading strategy on 3 years data, validate on out-of-sample 6 months, stress test with 2x volatility scenarios."
      },
      quiz: [
        {
          id: 'q1',
          question: "Look-ahead bias occurs when:",
          options: ["Using future information", "Using historical data", "Testing properly", "Avoiding overfitting"],
          correct: 0,
          explanation: "Correct! Look-ahead bias uses information that wouldn't have been available at the time of trading."
        },
        {
          id: 'q2',
          question: "Out-of-sample testing:",
          options: ["Uses all available data", "Validates on unseen data", "Overfits the model", "Ignores recent data"],
          correct: 1,
          explanation: "Correct! Out-of-sample testing validates strategy performance on data not used in development."
        }
      ]
    },
    4: {
      title: "Options Pricing Models",
      icon: "🧮",
      content: {
        keyPoints: [
          "Black-Scholes and extensions for crypto",
          "Stochastic volatility models (Heston, SABR)",
          "Jump diffusion and regime-switching models",
          "Model calibration and parameter estimation"
        ],
        example: "Calibrate Heston model to BTC option surface: vol-of-vol = 0.8, mean reversion = 2.5, correlation = -0.6."
      },
      quiz: [
        {
          id: 'q1',
          question: "Black-Scholes assumes:",
          options: ["Constant volatility", "Variable interest rates", "Jump processes", "Mean reversion"],
          correct: 0,
          explanation: "Correct! Black-Scholes assumes constant volatility, which is often violated in crypto markets."
        },
        {
          id: 'q2',
          question: "Stochastic volatility models address:",
          options: ["Constant vol assumption", "Interest rate changes", "Dividend payments", "Strike selection"],
          correct: 0,
          explanation: "Correct! Stochastic volatility models allow volatility to change randomly over time."
        }
      ]
    },
    5: {
      title: "Risk Management Automation",
      icon: "🛡️",
      content: {
        keyPoints: [
          "Real-time portfolio monitoring and alerts",
          "Automated hedging and rebalancing",
          "Position limits and exposure controls",
          "Stress testing and scenario analysis"
        ],
        example: "System auto-hedges when portfolio delta > ±100, rebalances gamma exposure every 30 minutes."
      },
      quiz: [
        {
          id: 'q1',
          question: "Automated risk management provides:",
          options: ["Manual oversight only", "Real-time protection", "Delayed responses", "Increased risk"],
          correct: 1,
          explanation: "Correct! Automated systems provide real-time risk monitoring and immediate protective actions."
        },
        {
          id: 'q2',
          question: "Dynamic hedging requires:",
          options: ["Monthly rebalancing", "Continuous monitoring", "Manual intervention", "Static positions"],
          correct: 1,
          explanation: "Correct! Dynamic hedging needs continuous monitoring and frequent adjustments."
        }
      ]
    },
    6: {
      title: "High-Frequency Trading",
      icon: "⚡",
      content: {
        keyPoints: [
          "Microsecond execution and market microstructure",
          "Latency arbitrage and co-location strategies",
          "Order book dynamics and liquidity provision",
          "Technology infrastructure requirements"
        ],
        example: "HFT system detects 1ms arbitrage between exchanges, executes 10,000 option trades capturing $0.01 edge per contract."
      },
      quiz: [
        {
          id: 'q1',
          question: "High-frequency trading focuses on:",
          options: ["Long-term trends", "Microsecond opportunities", "Fundamental analysis", "News events"],
          correct: 1,
          explanation: "Correct! HFT captures very short-term opportunities measured in microseconds."
        },
        {
          id: 'q2',
          question: "Co-location in HFT refers to:",
          options: ["Working together", "Physical proximity to exchanges", "Data sharing", "Joint ventures"],
          correct: 1,
          explanation: "Correct! Co-location places trading servers physically close to exchange servers for speed."
        }
      ]
    },
    7: {
      title: "Machine Learning Applications",
      icon: "🧠",
      content: {
        keyPoints: [
          "Feature engineering and data preprocessing",
          "Supervised vs unsupervised learning approaches",
          "Neural networks for option pricing",
          "Reinforcement learning for trading strategies"
        ],
        example: "Train LSTM neural network on vol surface data to predict next-day implied volatility movements."
      },
      quiz: [
        {
          id: 'q1',
          question: "Feature engineering involves:",
          options: ["Random data selection", "Transforming raw data into predictive features", "Ignoring data quality", "Using all available data"],
          correct: 1,
          explanation: "Correct! Feature engineering transforms raw data into meaningful predictive variables."
        },
        {
          id: 'q2',
          question: "Reinforcement learning is useful for:",
          options: ["Static pricing", "Dynamic trading strategies", "Historical analysis only", "Data storage"],
          correct: 1,
          explanation: "Correct! Reinforcement learning adapts trading strategies based on market feedback and rewards."
        }
      ]
    },
    8: {
      title: "Portfolio Optimization",
      icon: "🎯",
      content: {
        keyPoints: [
          "Mean-variance optimization and extensions",
          "Risk parity and factor-based approaches",
          "Transaction costs and turnover constraints",
          "Multi-objective optimization frameworks"
        ],
        example: "Optimize option portfolio: maximize Sharpe ratio subject to 10% VaR limit and 20% monthly turnover constraint."
      },
      quiz: [
        {
          id: 'q1',
          question: "Mean-variance optimization balances:",
          options: ["Speed and accuracy", "Return and risk", "Cost and benefit", "Time and money"],
          correct: 1,
          explanation: "Correct! Mean-variance optimization finds the optimal balance between expected return and risk."
        },
        {
          id: 'q2',
          question: "Turnover constraints limit:",
          options: ["Portfolio returns", "Trading frequency", "Risk exposure", "Investment choices"],
          correct: 1,
          explanation: "Correct! Turnover constraints limit how frequently positions can be changed to control costs."
        }
      ]
    },
    9: {
      title: "System Architecture and Operations",
      icon: "🏗️",
      content: {
        keyPoints: [
          "Scalable system design and microservices",
          "Data management and real-time processing",
          "Fault tolerance and disaster recovery",
          "Performance monitoring and optimization"
        ],
        example: "Deploy containerized trading system: 99.99% uptime, process 1M quotes/second, sub-millisecond latency."
      },
      quiz: [
        {
          id: 'q1',
          question: "Microservices architecture provides:",
          options: ["Monolithic design", "Scalable modular components", "Single point of failure", "Complex deployment"],
          correct: 1,
          explanation: "Correct! Microservices break systems into scalable, independently deployable components."
        },
        {
          id: 'q2',
          question: "Fault tolerance ensures:",
          options: ["Perfect systems", "Graceful failure handling", "No monitoring needed", "Single server dependency"],
          correct: 1,
          explanation: "Correct! Fault tolerance allows systems to continue operating even when components fail."
        }
      ]
    }
  };

  const currentModuleContent = moduleContent[currentLesson];
  if (!currentModuleContent) return null;

  if (currentState.phase === 'intro') {
    return (
      <div className="space-y-6">
        <div className="text-center">
          <div className="text-6xl mb-4">{currentModuleContent.icon}</div>
          <h3 className="text-white text-2xl font-bold mb-2">Module {currentLesson}: {currentModuleContent.title}</h3>
          <p className="text-gray-400">Advanced algorithmic trading systems</p>
        </div>

        <div className="bg-atticus-navy-800 rounded-lg p-6 space-y-4">
          <div className="border-l-4 border-cyan-500 pl-4">
            <h4 className="text-white font-bold mb-2">🤖 Technical Concepts</h4>
            <div className="space-y-2">
              {currentModuleContent.content.keyPoints.map((point, index) => (
                <p key={index} className="text-gray-300 text-sm">• {point}</p>
              ))}
            </div>
          </div>

          <div className="bg-teal-500/20 border border-teal-500/50 rounded-lg p-4">
            <h5 className="text-teal-400 font-bold mb-2">⚡ System Example</h5>
            <p className="text-white text-sm">{currentModuleContent.content.example}</p>
          </div>
        </div>

        <button
          onClick={() => updateTutorialState({ phase: 'quiz' })}
          className="w-full bg-cyan-600 hover:bg-cyan-700 text-white py-3 rounded-lg font-bold transition-colors"
        >
          Test Algorithmic Knowledge →
        </button>
      </div>
    );
  }

  if (currentState.phase === 'quiz') {
    return (
      <div className="space-y-6">
        <div className="text-center">
          <h3 className="text-white text-2xl font-bold mb-2">Knowledge Check: {currentModuleContent.title}</h3>
          <p className="text-gray-400">Master these algorithmic concepts</p>
        </div>

        {currentModuleContent.quiz.map((question, qIndex) => {
          const userAnswer = currentState.quizAnswers[question.id];
          const isAnswered = userAnswer !== undefined;
          const isCorrect = userAnswer === question.correct;

          return (
            <div key={question.id} className="bg-atticus-navy-800 rounded-lg p-6">
              <div className="mb-4">
                <h4 className="text-white font-bold mb-3">Question {qIndex + 1}: {question.question}</h4>
                <div className="space-y-2">
                  {question.options.map((option, oIndex) => (
                    <button
                      key={oIndex}
                      onClick={() => updateTutorialState({ 
                        quizAnswers: { ...currentState.quizAnswers, [question.id]: oIndex }
                      })}
                      disabled={isAnswered}
                      className={`w-full text-left p-3 rounded-lg border-2 transition-all ${
                        isAnswered
                          ? oIndex === question.correct 
                            ? 'bg-green-500/20 border-green-500 text-green-300'
                            : oIndex === userAnswer
                              ? 'bg-red-500/20 border-red-500 text-red-300'
                              : 'bg-gray-700 border-gray-600 text-gray-400'
                          : 'bg-atticus-navy-700 border-gray-600 text-gray-300 hover:border-cyan-500 hover:text-white'
                      }`}
                    >
                      {option}
                      {isAnswered && oIndex === question.correct && <span className="float-right text-green-400">✓</span>}
                      {isAnswered && oIndex === userAnswer && oIndex !== question.correct && <span className="float-right text-red-400">✗</span>}
                    </button>
                  ))}
                </div>
              </div>

              {isAnswered && (
                <div className={`p-3 rounded-lg border ${isCorrect ? 'bg-green-500/20 border-green-500' : 'bg-red-500/20 border-red-500'}`}>
                  <p className={`text-sm ${isCorrect ? 'text-green-300' : 'text-red-300'}`}>
                    {isCorrect ? '✅ Correct! ' : '❌ Incorrect. '}{question.explanation}
                  </p>
                </div>
              )}
            </div>
          );
        })}

        {Object.keys(currentState.quizAnswers).length === currentModuleContent.quiz.length && (
          <div className="text-center">
            {currentModuleContent.quiz.every(q => currentState.quizAnswers[q.id] === q.correct) ? (
              <div className="space-y-4">
                <div className="bg-green-500/20 border border-green-500 rounded-lg p-4">
                  <div className="text-green-400 font-bold text-lg">🎉 Module {currentLesson} Complete!</div>
                  <div className="text-green-300">You've mastered {currentModuleContent.title}!</div>
                </div>
                <button
                  onClick={() => {
                    const moduleXP = currentTutorialInfo.xp_per_module;
                    setUserStats(prev => ({
                      ...prev,
                      completed_lessons: [...prev.completed_lessons, `algorithmic_options_module_${currentLesson}`],
                      total_xp: prev.total_xp + moduleXP,
                      badges: currentLesson === 1 ? [...prev.badges, 'Algorithm Developer'] : prev.badges
                    }));

                    if (currentLesson < currentTutorialInfo.modules) {
                      setCurrentLesson(currentLesson + 1);
                      updateTutorialState({ phase: 'intro', quizAnswers: {} });
                    } else {
                      setTutorialComplete(true);
                      setUserStats(prev => ({
                        ...prev,
                        completed_tutorials: [...prev.completed_tutorials, 'algorithmic_options'],
                        certification_progress: Math.min(prev.certification_progress + 15, 100),
                        badges: [...prev.badges, 'Algorithmic Trading Master']
                      }));
                    }
                  }}
                  className="bg-green-600 hover:bg-green-700 text-white px-8 py-3 rounded-lg font-bold transition-colors"
                >
                  {currentLesson < currentTutorialInfo.modules ? `Continue to Module ${currentLesson + 1} →` : 'Complete Tutorial →'}
                </button>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="bg-orange-500/20 border border-orange-500 rounded-lg p-4">
                  <div className="text-orange-400 font-bold">📖 Review Required</div>
                  <div className="text-orange-300">Please review the material and try again.</div>
                </div>
                <button
                  onClick={() => updateTutorialState({ quizAnswers: {}, phase: 'intro' })}
                  className="bg-orange-600 hover:bg-orange-700 text-white px-6 py-3 rounded-lg font-bold transition-colors"
                >
                  Review Module {currentLesson}
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    );
  }

  return null;
};

export default AlgorithmicOptions;
